import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TrendingUp, DollarSign, Target, Clock, BarChart3, Activity } from 'lucide-react';

// ============================================
// COMPREHENSIVE ANALYTICS COMPONENT
// Displays comprehensive trading analytics with 4 main sections
// ============================================

interface WinRateStatistics {
  overall: number;
  totalWins: number;
  totalLosses: number;
  byGroup: Array<{
    chatId: number;
    groupName: string;
    wins: number;
    losses: number;
    winRate: number;
  }>;
  byTrader: Array<{
    userId: number;
    username: string | null;
    wins: number;
    losses: number;
    winRate: number;
  }>;
  bestTokens: Array<{
    symbol: string;
    address: string;
    wins: number;
    losses: number;
    totalPnL: number;
    winRate: number;
  }>;
  worstTokens: Array<{
    symbol: string;
    address: string;
    wins: number;
    losses: number;
    totalPnL: number;
    winRate: number;
  }>;
}

interface TradeVolumeMetrics {
  totalVolumeSol: number;
  totalVolumeUsd: number;
  averageTradeSize: number;
  largestTrades: Array<{
    id: number;
    tokenSymbol: string;
    tokenAddress: string;
    amountUsd: number;
    timestamp: string;
    chatId: number;
    callerUsername: string | null;
  }>;
  byGroup: Array<{
    chatId: number;
    groupName: string;
    volumeSol: number;
    volumeUsd: number;
  }>;
  overTime: Array<{
    date: string;
    volumeSol: number;
    volumeUsd: number;
  }>;
}

interface ROIPerformanceMetrics {
  averageRoi: number;
  bestTrades: Array<{
    id: number;
    tokenSymbol: string;
    tokenAddress: string;
    roi: number;
    pnl: number;
    entryPrice: number;
    exitPrice: number;
    timestamp: string;
  }>;
  worstTrades: Array<{
    id: number;
    tokenSymbol: string;
    tokenAddress: string;
    roi: number;
    pnl: number;
    entryPrice: number;
    exitPrice: number;
    timestamp: string;
  }>;
  roiDistribution: Array<{
    range: string;
    count: number;
  }>;
}

interface TimeBasedAnalytics {
  activityByHour: Array<{
    hour: number;
    count: number;
  }>;
  activityByDay: Array<{
    date: string;
    count: number;
  }>;
  activityByWeek: Array<{
    week: string;
    count: number;
  }>;
  mostActiveHours: Array<{
    hour: number;
    count: number;
  }>;
  growthTrends: Array<{
    date: string;
    totalGroups: number;
    tradingGroups: number;
    totalTvmUsd: number;
    totalTrades: number;
    realizedPnl: number;
  }>;
  newGroupsOverTime: Array<{
    date: string;
    count: number;
  }>;
}

interface ComprehensiveAnalyticsData {
  winRateStatistics: WinRateStatistics;
  tradeVolumeMetrics: TradeVolumeMetrics;
  roiPerformanceMetrics: ROIPerformanceMetrics;
  timeBasedAnalytics: TimeBasedAnalytics;
  summary: {
    totalTrades: number;
    closedTrades: number;
    activePositions: number;
    totalGroups: number;
    solPrice: number;
  };
}

// Container styled to match HeroCard
const AnalyticsContainer = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  height: 470px;
  display: flex;
  flex-direction: column;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 200px;
  }
`;

const AnalyticsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const AnalyticsTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 20px;
    height: 20px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
  
  svg {
    width: 12px;
    height: 12px;
    margin-right: 4px;
    vertical-align: middle;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  text-align: center;
`;

const StatValue = styled.div<{ $positive?: boolean; $negative?: boolean }>`
  font-size: 20px;
  font-weight: 700;
  color: ${props => {
    if (props.$positive) return '#10b981';
    if (props.$negative) return '#ef4444';
    return '#ffffff';
  }};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-top: 12px;
`;

const TableHeader = styled.thead`
  background: rgba(0, 0, 0, 0.3);
`;

const TableHeaderCell = styled.th`
  padding: 8px;
  text-align: left;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const TableCell = styled.td<{ $positive?: boolean; $negative?: boolean }>`
  padding: 8px;
  color: ${props => {
    if (props.$positive) return '#10b981';
    if (props.$negative) return '#ef4444';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  font-weight: ${props => (props.$positive || props.$negative) ? '600' : '400'};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 200px;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const SideBySide = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 12px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ef4444;
  font-size: 13px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  padding: 20px;
  text-align: center;
`;

// Format helpers
const formatCurrency = (value: number): string => {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatUsername = (username: string | null, userId: number): string => {
  if (username) return `@${username}`;
  return `User ${userId}`;
};

// Fetch function
const fetchComprehensiveAnalytics = async (): Promise<ComprehensiveAnalyticsData> => {
  const apiUrl = (import.meta.env as any).VITE_SENTRY_BOT_API_URL;
  if (!apiUrl) {
    throw new Error('Sentry Bot API URL not configured');
  }
  
  const response = await fetch(`${apiUrl}/api/analytics/comprehensive`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch comprehensive analytics' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Handle wrapped response
  if (data.success && data.data) {
    return data.data;
  }
  
  // Handle direct response
  return data;
};

type TabType = 'winrate' | 'volume' | 'roi' | 'time';

const ComprehensiveAnalytics: React.FC = () => {
  const [data, setData] = useState<ComprehensiveAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('winrate');
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchComprehensiveAnalytics();
        setData(result);
      } catch (err: any) {
        console.error('[ComprehensiveAnalytics] Error fetching data:', err);
        setError(err.message || 'Failed to load comprehensive analytics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  const renderWinRateSection = () => {
    if (!data) return null;
    
    const { winRateStatistics } = data;
    
    return (
      <Section>
        {/* Overall Win Rate */}
        <StatsGrid>
          <StatCard>
            <StatValue $positive={winRateStatistics.overall >= 60}>
              {formatPercentage(winRateStatistics.overall)}
            </StatValue>
            <StatLabel>Overall Win Rate</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{winRateStatistics.totalWins}</StatValue>
            <StatLabel>Total Wins</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{winRateStatistics.totalLosses}</StatValue>
            <StatLabel>Total Losses</StatLabel>
          </StatCard>
        </StatsGrid>
        
        {/* Win Rate by Group */}
        {winRateStatistics.byGroup.length > 0 && (
          <>
            <SectionTitle>Win Rate by Group</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Group</TableHeaderCell>
                  <TableHeaderCell>Wins</TableHeaderCell>
                  <TableHeaderCell>Losses</TableHeaderCell>
                  <TableHeaderCell>Win Rate</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {winRateStatistics.byGroup.slice(0, 5).map((group) => (
                  <TableRow key={group.chatId}>
                    <TableCell>{group.groupName}</TableCell>
                    <TableCell>{group.wins}</TableCell>
                    <TableCell>{group.losses}</TableCell>
                    <TableCell $positive={group.winRate >= 60}>
                      {formatPercentage(group.winRate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        
        {/* Best/Worst Tokens */}
        <SideBySide>
          <div>
            <SectionTitle>Best Tokens</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Token</TableHeaderCell>
                  <TableHeaderCell>PnL</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {winRateStatistics.bestTokens.slice(0, 5).map((token) => (
                  <TableRow key={token.address}>
                    <TableCell>{token.symbol}</TableCell>
                    <TableCell $positive>{formatCurrency(token.totalPnL)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div>
            <SectionTitle>Worst Tokens</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Token</TableHeaderCell>
                  <TableHeaderCell>PnL</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {winRateStatistics.worstTokens.slice(0, 5).map((token) => (
                  <TableRow key={token.address}>
                    <TableCell>{token.symbol}</TableCell>
                    <TableCell $negative>{formatCurrency(token.totalPnL)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SideBySide>
      </Section>
    );
  };
  
  const renderVolumeSection = () => {
    if (!data) return null;
    
    const { tradeVolumeMetrics } = data;
    
    return (
      <Section>
        {/* Summary Stats */}
        <StatsGrid>
          <StatCard>
            <StatValue>{formatCurrency(tradeVolumeMetrics.totalVolumeUsd)}</StatValue>
            <StatLabel>Total Volume</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatCurrency(tradeVolumeMetrics.averageTradeSize)}</StatValue>
            <StatLabel>Avg Trade Size</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{tradeVolumeMetrics.totalVolumeSol.toFixed(2)}</StatValue>
            <StatLabel>Volume (SOL)</StatLabel>
          </StatCard>
        </StatsGrid>
        
        {/* Volume Over Time Chart */}
        {tradeVolumeMetrics.overTime.length > 0 && (
          <>
            <SectionTitle>Volume Over Time</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tradeVolumeMetrics.overTime.slice(-30)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                    tickFormatter={formatDate}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number) => formatCurrency(value)}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="volumeUsd" 
                    stroke="#10b981" 
                    fill="#10b981" 
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
        
        {/* Largest Trades */}
        {tradeVolumeMetrics.largestTrades.length > 0 && (
          <>
            <SectionTitle>Largest Trades</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Token</TableHeaderCell>
                  <TableHeaderCell>Amount</TableHeaderCell>
                  <TableHeaderCell>Date</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {tradeVolumeMetrics.largestTrades.slice(0, 5).map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell>{trade.tokenSymbol}</TableCell>
                    <TableCell>{formatCurrency(trade.amountUsd)}</TableCell>
                    <TableCell>{formatDate(trade.timestamp)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Section>
    );
  };
  
  const renderROISection = () => {
    if (!data) return null;
    
    const { roiPerformanceMetrics } = data;
    
    return (
      <Section>
        {/* Average ROI */}
        <StatsGrid>
          <StatCard>
            <StatValue $positive={roiPerformanceMetrics.averageRoi >= 0}>
              {formatPercentage(roiPerformanceMetrics.averageRoi)}
            </StatValue>
            <StatLabel>Average ROI</StatLabel>
          </StatCard>
        </StatsGrid>
        
        {/* ROI Distribution Chart */}
        {roiPerformanceMetrics.roiDistribution.length > 0 && (
          <>
            <SectionTitle>ROI Distribution</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roiPerformanceMetrics.roiDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="range" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
        
        {/* Best/Worst Trades */}
        <SideBySide>
          <div>
            <SectionTitle>Best Trades</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Token</TableHeaderCell>
                  <TableHeaderCell>ROI</TableHeaderCell>
                  <TableHeaderCell>PnL</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {roiPerformanceMetrics.bestTrades.slice(0, 5).map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell>{trade.tokenSymbol}</TableCell>
                    <TableCell $positive>{formatPercentage(trade.roi)}</TableCell>
                    <TableCell $positive>{formatCurrency(trade.pnl)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div>
            <SectionTitle>Worst Trades</SectionTitle>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Token</TableHeaderCell>
                  <TableHeaderCell>ROI</TableHeaderCell>
                  <TableHeaderCell>PnL</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {roiPerformanceMetrics.worstTrades.slice(0, 5).map((trade) => (
                  <TableRow key={trade.id}>
                    <TableCell>{trade.tokenSymbol}</TableCell>
                    <TableCell $negative>{formatPercentage(trade.roi)}</TableCell>
                    <TableCell $negative>{formatCurrency(trade.pnl)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SideBySide>
      </Section>
    );
  };
  
  const renderTimeSection = () => {
    if (!data) return null;
    
    const { timeBasedAnalytics } = data;
    
    return (
      <Section>
        {/* Activity by Hour Chart */}
        {timeBasedAnalytics.activityByHour.length > 0 && (
          <>
            <SectionTitle>Trading Activity by Hour</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeBasedAnalytics.activityByHour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" fill="#a855f7" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
        
        {/* Activity Over Time */}
        {timeBasedAnalytics.activityByDay.length > 0 && (
          <>
            <SectionTitle>Activity Over Time (Daily)</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeBasedAnalytics.activityByDay.slice(-30)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                    tickFormatter={formatDate}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
        
        {/* Growth Trends */}
        {timeBasedAnalytics.growthTrends.length > 0 && (
          <>
            <SectionTitle>Growth Trends</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeBasedAnalytics.growthTrends.slice(-30)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                    tickFormatter={formatDate}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    tick={{ fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="totalGroups" 
                    stroke="#a855f7" 
                    strokeWidth={2}
                    dot={false}
                    name="Total Groups"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="totalTrades" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                    name="Total Trades"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </>
        )}
      </Section>
    );
  };
  
  const renderContent = () => {
    switch (activeTab) {
      case 'winrate':
        return renderWinRateSection();
      case 'volume':
        return renderVolumeSection();
      case 'roi':
        return renderROISection();
      case 'time':
        return renderTimeSection();
      default:
        return renderWinRateSection();
    }
  };
  
  if (loading) {
    return (
      <AnalyticsContainer>
        <AnalyticsHeader>
          <AnalyticsTitle>
            <BarChart3 /> Comprehensive Analytics
          </AnalyticsTitle>
        </AnalyticsHeader>
        <LoadingState>Loading comprehensive analytics...</LoadingState>
      </AnalyticsContainer>
    );
  }
  
  if (error) {
    return (
      <AnalyticsContainer>
        <AnalyticsHeader>
          <AnalyticsTitle>
            <BarChart3 /> Comprehensive Analytics
          </AnalyticsTitle>
        </AnalyticsHeader>
        <ErrorState>Error: {error}</ErrorState>
      </AnalyticsContainer>
    );
  }
  
  if (!data) {
    return (
      <AnalyticsContainer>
        <AnalyticsHeader>
          <AnalyticsTitle>
            <BarChart3 /> Comprehensive Analytics
          </AnalyticsTitle>
        </AnalyticsHeader>
        <EmptyState>No data available</EmptyState>
      </AnalyticsContainer>
    );
  }
  
  return (
    <AnalyticsContainer>
      <AnalyticsHeader>
        <AnalyticsTitle>
          <BarChart3 /> Comprehensive Analytics
        </AnalyticsTitle>
      </AnalyticsHeader>
      
      <TabsContainer>
        <Tab $active={activeTab === 'winrate'} onClick={() => setActiveTab('winrate')}>
          <Target /> Win Rate
        </Tab>
        <Tab $active={activeTab === 'volume'} onClick={() => setActiveTab('volume')}>
          <DollarSign /> Volume
        </Tab>
        <Tab $active={activeTab === 'roi'} onClick={() => setActiveTab('roi')}>
          <TrendingUp /> ROI
        </Tab>
        <Tab $active={activeTab === 'time'} onClick={() => setActiveTab('time')}>
          <Clock /> Time
        </Tab>
      </TabsContainer>
      
      <ContentArea>
        {renderContent()}
      </ContentArea>
    </AnalyticsContainer>
  );
};

export default ComprehensiveAnalytics;